import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { invalidateAfterUpload } from '../../lib/queries.ts';
import type { ItemKind } from './useSelection.ts';

/**
 * Copy, Cut and Paste, as a file explorer does them.
 *
 * Held above the Drive page rather than inside it, for the same reason the
 * upload queue is: you copy in one folder and paste in another, and a
 * clipboard that lived in the page would be emptied by the navigation between
 * those two acts.
 *
 * Cut does not move anything until Paste. Nothing is hidden or greyed in the
 * meantime either — the rows stay exactly where they are, because a cut that
 * is never pasted has to leave the Drive as it found it, and an explorer that
 * removes the row on Ctrl+X is lying about what has happened.
 */

export interface ClipboardPayload {
  folderIds: string[];
  fileIds: string[];
  spreadsheetIds: string[];
  documentIds: string[];
}

interface Clipboard {
  mode: 'copy' | 'cut';
  payload: ClipboardPayload;
  /** For the "N items ready to paste" line, without refetching anything. */
  count: number;
}

interface ClipboardApi {
  clipboard: Clipboard | null;
  copy(payload: ClipboardPayload): void;
  cut(payload: ClipboardPayload): void;
  clear(): void;
  /** True while a paste is in flight, so the UI can disable the action. */
  pasting: boolean;
  /**
   * Paste into a folder. Returns what happened, so the caller can say so —
   * or null when there was nothing on the clipboard.
   */
  paste(folderId: string | null): Promise<{ mode: 'copy' | 'cut'; count: number } | null>;
}

const ClipboardContext = createContext<ClipboardApi | null>(null);

export function useDriveClipboard(): ClipboardApi {
  const api = useContext(ClipboardContext);
  if (!api) throw new Error('useDriveClipboard must be used inside DriveClipboardProvider');
  return api;
}

/** How many things a payload names. */
export function countPayload(payload: ClipboardPayload): number {
  return (
    payload.folderIds.length +
    payload.fileIds.length +
    payload.spreadsheetIds.length +
    payload.documentIds.length
  );
}

/** One item, in the shape the endpoints want. */
export function payloadFor(kind: ItemKind, id: string): ClipboardPayload {
  return {
    folderIds: kind === 'folder' ? [id] : [],
    fileIds: kind === 'file' ? [id] : [],
    spreadsheetIds: kind === 'spreadsheet' ? [id] : [],
    documentIds: kind === 'document' ? [id] : [],
  };
}

export function DriveClipboardProvider({ children }: { children: ReactNode }) {
  const [clipboard, setClipboard] = useState<Clipboard | null>(null);
  const [pasting, setPasting] = useState(false);
  const client = useQueryClient();

  const copy = useCallback((payload: ClipboardPayload) => {
    const count = countPayload(payload);
    if (count > 0) setClipboard({ mode: 'copy', payload, count });
  }, []);

  const cut = useCallback((payload: ClipboardPayload) => {
    const count = countPayload(payload);
    if (count > 0) setClipboard({ mode: 'cut', payload, count });
  }, []);

  const clear = useCallback(() => setClipboard(null), []);

  const paste = useCallback(
    async (folderId: string | null) => {
      if (!clipboard || pasting) return null;
      setPasting(true);

      try {
        await api.post(
          clipboard.mode === 'copy' ? '/api/drive/bulk/copy' : '/api/drive/bulk/move',
          { ...clipboard.payload, folderId },
        );

        // Both ends of the operation change: the source folder loses rows on a
        // cut, and the destination gains them either way.
        await invalidateAfterUpload(client, folderId);
        await client.invalidateQueries({ queryKey: ['folders'] });
        await client.invalidateQueries({ queryKey: ['files'] });

        const done = { mode: clipboard.mode, count: clipboard.count };
        // A cut is spent once pasted — pasting it again would move things that
        // are no longer where the clipboard says. A copy stays, as it does
        // everywhere else, so it can be pasted into several folders.
        if (clipboard.mode === 'cut') setClipboard(null);
        return done;
      } finally {
        setPasting(false);
      }
    },
    [clipboard, pasting, client],
  );

  const value = useMemo<ClipboardApi>(
    () => ({ clipboard, copy, cut, clear, paste, pasting }),
    [clipboard, copy, cut, clear, paste, pasting],
  );

  return <ClipboardContext.Provider value={value}>{children}</ClipboardContext.Provider>;
}

/** Duplicate in place — a copy pasted straight back where it came from. */
export async function duplicateInPlace(
  payload: ClipboardPayload,
  folderId: string | null,
): Promise<void> {
  await api.post('/api/drive/bulk/copy', { ...payload, folderId });
}
